import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fireAwardEvent } from '../../lib/awardEvents';
import { downloadFile, safeFilename } from '../../lib/downloadFile';

interface Award {
  id: string;
  image: string;
  recipient: { name: string };
  badge: { name: string; issuer: { name: string } };
}

function EmbedSection({ award, viewUrl }: { award: Award; viewUrl: string }) {
  const [copied, setCopied] = useState<'img' | 'iframe' | null>(null);
  const imgSnippet = `<a href="${viewUrl}" target="_blank" rel="noopener"><img src="${award.image}" alt="${award.badge.name}" style="max-width:200px;height:auto;" /></a>`;
  const iframeSnippet = `<iframe src="${viewUrl}" width="640" height="480" frameborder="0" title="${award.badge.name}"></iframe>`;
  function copy(snippet: string, kind: 'img' | 'iframe') {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(kind);
      setTimeout(() => setCopied(null), 2000);
    });
  }
  return (
    <div className="border-t border-gray-200 pt-4 mt-2">
      <h3 className="text-sm font-semibold text-gray-900 mb-1">Embed on your site</h3>
      <p className="text-xs text-gray-500 mb-3">Drop one of these snippets into any page or email signature.</p>
      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-gray-700">Image (links to verification)</span>
            <button onClick={() => copy(imgSnippet, 'img')} className="text-xs text-primary-600 hover:underline">
              {copied === 'img' ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <pre className="text-[11px] bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">{imgSnippet}</pre>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-gray-700">Full iframe</span>
            <button onClick={() => copy(iframeSnippet, 'iframe')} className="text-xs text-primary-600 hover:underline">
              {copied === 'iframe' ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <pre className="text-[11px] bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">{iframeSnippet}</pre>
        </div>
      </div>
    </div>
  );
}

const API_URL = import.meta.env.VITE_CERTIFY_BASE_URL || import.meta.env.VITE_API_URL;

export function AwardShare() {
  const { guid } = useParams<{ guid: string }>();
  const { t } = useTranslation('common');
  const [award, setAward] = useState<Award | null>(null);
  const [shareText, setShareText] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({ recipients: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  useEffect(() => {
    if (!guid) return;
    Promise.all([
      fetch(`${API_URL}/certify-badge/award/${guid}`).then(r => r.json()),
      fetch(`${API_URL}/certify-badge/award/${guid}/share-text`).then(r => r.json()).catch(() => ({ text: '' }))
    ]).then(([awardData, shareData]) => {
      setAward(awardData);
      setShareText(shareData.text || `Check out this badge: ${awardData.badge?.name}`);
      setEmailForm(f => ({ ...f, subject: t('award.share.emailSubjectDefault') }));
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, [guid, t]);

  const viewUrl = `${window.location.origin}/awards/${guid}`;

  const copyLink = useCallback(() => {
    navigator.clipboard.writeText(viewUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    if (guid) fireAwardEvent(guid, 'share', 'copy');
  }, [viewUrl, guid]);

  const sendEmail = async () => {
    setSending(true);
    setEmailError(null);
    try {
      const res = await fetch(`${API_URL}/certify-badge/award/${guid}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipients: emailForm.recipients, subject: emailForm.subject, message: emailForm.message })
      });
      if (!res.ok) throw new Error((await res.json()).error || 'failed');
      setEmailModal(false);
      setEmailForm({ recipients: '', subject: t('award.share.emailSubjectDefault'), message: '' });
    } catch (err: unknown) {
      setEmailError(err instanceof Error ? err.message : t('award.share.emailError'));
    } finally {
      setSending(false);
    }
  };

  if (loading) return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="h-8 w-8 rounded-full border-4 border-indigo-200 border-t-indigo-600 animate-spin" />
    </div>
  );

  if (!award) return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <p className="text-gray-500">{t('award.notFound')}</p>
    </div>
  );

  const encodedUrl = encodeURIComponent(viewUrl);
  const encodedText = encodeURIComponent(shareText);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="md:flex">
          {/* Badge preview */}
          <div className="md:w-1/3 p-8 bg-gray-50 flex flex-col items-center justify-center">
            <img src={award.image} alt={award.badge.name} className="w-40 h-40 object-contain mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 text-center">{award.badge.name}</h2>
            <p className="text-sm text-gray-500 text-center">{award.badge.issuer.name}</p>
            <div className="mt-4 flex gap-2">
              <button type="button"
                onClick={() => { if (guid) fireAwardEvent(guid, 'download'); downloadFile(award.image, safeFilename(award.badge.name)); }}
                className="px-3 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-100">
                {t('award.view.download')}
              </button>
              <Link to={`/awards/${guid}`} className="px-3 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-100">
                {t('award.share.publicView')}
              </Link>
            </div>
          </div>

          {/* Share options */}
          <div className="md:w-2/3 p-8">
            <h1 className="text-xl font-bold text-gray-900 mb-1">{t('award.share.title')}</h1>
            <p className="text-sm text-gray-500 mb-6">{t('award.share.description')}</p>

            {/* Copy link */}
            <div className="mb-6">
              <div className="flex items-center gap-2">
                <input type="text" readOnly value={viewUrl}
                  className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600" />
                <button onClick={copyLink}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${copied ? 'bg-green-500 text-white' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
                  {copied ? t('award.share.copied') : t('award.share.copyLink')}
                </button>
              </div>
            </div>

            {/* Social buttons */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
              <a href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`} target="_blank" rel="noopener noreferrer"
                onClick={() => guid && fireAwardEvent(guid, 'share', 'twitter')}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-black text-white rounded-lg text-sm hover:bg-gray-800">
                Twitter/X
              </a>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`} target="_blank" rel="noopener noreferrer"
                onClick={() => guid && fireAwardEvent(guid, 'share', 'linkedin')}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0A66C2] text-white rounded-lg text-sm hover:bg-[#004182]">
                LinkedIn
              </a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`} target="_blank" rel="noopener noreferrer"
                onClick={() => guid && fireAwardEvent(guid, 'share', 'facebook')}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1877F2] text-white rounded-lg text-sm hover:bg-[#0C5DC7]">
                Facebook
              </a>
              <a href={`https://api.whatsapp.com/send?text=${encodedText}+${encodedUrl}`} target="_blank" rel="noopener noreferrer"
                onClick={() => guid && fireAwardEvent(guid, 'share', 'whatsapp')}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#25D366] text-white rounded-lg text-sm hover:bg-[#1DA851]">
                WhatsApp
              </a>
              <a href={`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`} target="_blank" rel="noopener noreferrer"
                onClick={() => guid && fireAwardEvent(guid, 'share', 'telegram')}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0088cc] text-white rounded-lg text-sm hover:bg-[#006daa]">
                Telegram
              </a>
              <button onClick={() => { setEmailModal(true); if (guid) fireAwardEvent(guid, 'share', 'email'); }}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-600 text-white rounded-lg text-sm hover:bg-gray-700">
                {t('award.share.sendEmail')}
              </button>
            </div>

            {/* Embed code */}
            <EmbedSection award={award} viewUrl={viewUrl} />
          </div>
        </div>
      </div>

      {/* Email modal */}
      {emailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">{t('award.share.sendEmail')}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('award.share.emailRecipients')}</label>
                <input type="text" value={emailForm.recipients}
                  onChange={e => setEmailForm(f => ({ ...f, recipients: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="email1@example.com, email2@example.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('award.share.emailSubject')}</label>
                <input type="text" value={emailForm.subject}
                  onChange={e => setEmailForm(f => ({ ...f, subject: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('award.share.emailMessage')}</label>
                <textarea value={emailForm.message}
                  onChange={e => setEmailForm(f => ({ ...f, message: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={4} />
              </div>
              {emailError && <p className="text-red-500 text-sm">{emailError}</p>}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEmailModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={sendEmail} disabled={sending || !emailForm.recipients}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50">
                {sending ? '...' : t('award.share.emailSend')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
